import React, { useMemo } from "react";
import { change, Field, getFormValues } from "redux-form";
import { useSelector, useDispatch } from "react-redux";
import { ReduxControls } from "rhombus-react";

import { Assets } from "reducers/Assets";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { ASSET_TYPES } from "utils/constants";
import { noop } from "utils/common";
import {
	mapStringToOption,
	normalizeOptionToString
} from "social/utils";
import { getGoogleLanguageOptions } from "selectors/Social2";

import AssetSelect from "social/AssetSelect";

import { GOOGLE_DEMAND_GEN_CAROUSEL_CONSTRAINTS } from "social/validators/google";

import "./CarouselCardForm.css";

const {
	IMAGE_ACCEPT,
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_LANDING_PAGE,
} = GOOGLE_DEMAND_GEN_CAROUSEL_CONSTRAINTS;
const ASPECT_RATIO_TIPS = {
	horizontal: "1.91:1, 1200x628 and up recommended",
	square: "1:1, 1200x1200 and up recommended",
	vertical: "4:5, 960x1200 and up recommended",
};

export const GOOGLE_DEMAND_GEN_CAROUSEL_CARD_FIELDS = {
	headline: {
		name: "headline",
		label: "Headline",
		className: "full-width cypress-headline",
		placeholder: "Enter Headline...",
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_HEADLINE,
		showCharCount: true,
		required: true,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "Enter URL...",
		component: ReduxControls.Input,
		className: "full-width",
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: `Redirects to different domains are not permitted.`,
		},
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
		required: true,
	},
	mobileLandingPage: {
		name: "mobileLandingPage",
		label: "Mobile Landing Page",
		placeholder: "Enter URL...",
		component: ReduxControls.Input,
		className: "full-width",
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
	},
	language: {
		name: "language",
		label: "Language",
		component: ReduxControls.Select,
		className: "half-width",
		menuPlacement: "top",
		placeholder: "Select Language...",
		isClearable: false,
		onMenuScrollToBottom: noop,
	},
	cta: {
		name: "cta",
		label: "CTA",
		component: ReduxControls.Select,
		required: true,
		className: "half-width",
		menuPlacement: "top",
		placeholder: "Select CTA...",
		isClearable: false,
		onMenuScrollToBottom: noop,
		normalize: normalizeOptionToString,
		format: mapStringToOption,
	},
};

function ImageFields(props){
	const {
		libraryId,
		navigationActions,
		cardPrefix,
		requiredAspectRatios = [],
		isViewOnly,
		showPristineErrors,
		configureFilter,
	}  = props;

	const fields = requiredAspectRatios.map(ratio => (
		<Field
			key={ratio}
			name={`${cardPrefix}.images.${ratio}`}
			label={ratio}
			required
			showName
			component={AssetSelect}
			className="cypress-image-select"
			libraryId={libraryId}
			assetType={ASSET_TYPES.IMAGE}
			navigationActions={navigationActions}
			accept={IMAGE_ACCEPT}
			helpTip={ASPECT_RATIO_TIPS[ratio]}
			disabled={isViewOnly}
			showPristineErrors={showPristineErrors}
			configureFilter={configureFilter}
		/>
	));

	return (
		<div className="image-fields">
			{fields}
		</div>
	);
}

function fieldGenerator(props) {
	const {
		cardPrefix,
		isViewOnly = false,
		showPristineErrors = false,
		languageOptions = [],
		ctaOptions = [],
		onLanguageChange = noop,
	} = props;

	return (name) => {
		const fieldProps = GOOGLE_DEMAND_GEN_CAROUSEL_CARD_FIELDS[name];

		let extraProps = {
			disabled: isViewOnly,
			showPristineErrors,
		};

		if(name === "language"){
			extraProps.options = languageOptions;
			extraProps.onChange = onLanguageChange;
		}
		if(name === "cta"){
			extraProps.options = ctaOptions;
		}

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		return (
			<Field
				key={fieldProps.label}
				{...fieldProps}
				{...extraProps}
				name = {`${cardPrefix}.${fieldProps.name}`}
			/>
		);
	};
}


export default function GoogleDemandGenCarouselCardForm(props) {
	const {
		libraryId,
		navigationActions,
		adType,
		isViewOnly,
		showPristineErrors,
	} = props;

	const dispatch = useDispatch();

	const values = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {});

	const currentCard = values?.currentCard || 0;
	const cardPrefix = `cards[${currentCard}]`;
	const cardValues = values?.cards?.[currentCard] || {};
	const requiredAspectRatios = Object.keys(values.requiredAspectRatios || {}).filter(name => values.requiredAspectRatios[name]);

	const selectedLanguage = cardValues.language?.id || "en";
	const selectedCTA = cardValues.cta;
	const locales = useSelector(state => state.social.googleLocales.locales);
	const languageOptions = useSelector(getGoogleLanguageOptions);

	const selectedCardImages = useMemo(() => {
		/*
			This collects all of the selected image IDs from every card, and returns them as an array.

			The cards are structured like:
			```
				[
					{
						images: {
							square: { id, ... },
							vertical: { id, ... },
							horizontal: null, // This can happen if the user deletes a selection
						}
					},
					...
				]
			```
		*/
		const imageSet = new Set();
		const cards = values?.cards || [];
		cards.forEach((card) => {
			Object.values(card.images || {}).forEach(image => image?.id && imageSet.add(image.id));
		});

		return [...imageSet];
	}, [values.cards]);

	function configureFilter(){
		dispatch(Assets.setSort("lastModified", "DESC"));
		dispatch(Assets.updateColumnFilter("assetType", { params: ["Image"]}));
		if(selectedCardImages.length){
			// This removes already selected images from the select Assets grid
			dispatch(Assets.updateColumnFilter("id", { params: selectedCardImages }));
		}
	}

	const ctaOptions = locales[selectedLanguage]?.ctas?.map(cta => mapStringToOption(cta.name)) || [];

	function onLanguageChange(alsoValue, value, previousValue){
		/*
			When the user selects a new language, we have to update the CTA value to the equivalent ID,
			which we don't have directly, so we have to find it based on the previous language and CTA name.
		*/
		const prevLocale = previousValue?.id || "en";
		const ctaId = locales[prevLocale]?.ctas?.find(cta => cta.name === selectedCTA)?.id;

		const newLocale = value?.id;
		const equivalentCTA = locales[newLocale]?.ctas?.find(cta => cta.id === ctaId)?.name;

		dispatch(change(SOCIAL_WIZARD, `${adType}.${cardPrefix}.cta`, equivalentCTA));
	}

	const generateFields = fieldGenerator({
		cardPrefix,
		isViewOnly,
		showPristineErrors,
		languageOptions,
		ctaOptions,
		onLanguageChange,
	});
	const fields = useMemo(() => Object.keys(GOOGLE_DEMAND_GEN_CAROUSEL_CARD_FIELDS).map(generateFields),
		[currentCard, selectedLanguage, languageOptions, ctaOptions]
	);

	return (
		<div className="carousel-card-form">
			<div className="fields">
				<ImageFields {...{
					libraryId,
					cardPrefix,
					requiredAspectRatios,
					navigationActions,
					isViewOnly,
					showPristineErrors,
					configureFilter,
				}} />
				{fields}
			</div>
		</div>
	);
}

GoogleDemandGenCarouselCardForm.displayName = "GoogleDemandGenCarouselCardForm";
